<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="isDisabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    open-on-click
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="props" data-cy="list-podNodeItem-actions-deleteSelected-activator" />
    </template>

    <div class="vb-dialog vb-dialog-danger">
      <v-card v-if="waitingForUsersCount">
        <v-card-title class="d-flex justify-space-between">
          <span></span>

          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="errorMessage" class="bg-error">
            {{ errorMessage }}
          </div>
          <div v-if="errorTranslatable" class="bg-error">
            {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
          </div>
          <div class="text-center pt-5" v-else>
            <v-progress-circular class="float-left"
                                 indeterminate
                                 color="primary"
                                 :aria-label="$t('routes.node.loading')">
            </v-progress-circular>
            <h3 class="pl-2 mt-2 float-left">{{ $t('routes.node.loading') }}</h3>
          </div>

        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancel" data-cy="dialog-deleteNode-false">
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-if="noThingsSelected && shouldShowNoItems"
              data-cy="dialog-deleteSelectedNoFiles-modal">
        <v-card-title class="d-flex justify-space-between">
          {{ $t('routes.node.deleteSelectedDialogNoFiles') }}

          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dialog = false"
                 data-cy="dialog-deleteSelectedNoFiles-false">{{ $t('common.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>


      <v-card v-if="!waitingForUsersCount && !noThingsSelected"
              data-cy="dialog-deleteSelectedItems-modal">
        <v-card-title class="d-flex justify-space-between">
          {{ $t('routes.node.confirmDeletion') }}

          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-5">
          <template v-if="affectedFtpUsers.length > 0">
            <div class="tw-warning">
              <v-icon>mdi-alert-box</v-icon>
              <span>{{ $t('routes.node.deleteFtpUsersWarning') }}</span>
              <ul class="mx-5 mt-1">
                <li v-for="group in affectedFtpUsersByNode" :key="group.nodeId">
                  <router-link :to="'/node/' + group.nodeId">{{ group.nodeName }}</router-link>
                  ({{ group.usernames.join(', ') }})
                </li>
              </ul>
            </div>
          </template>
          <template v-else>
          <div>
            <div>
              {{
                deletionType === 'selected' ? $t('routes.node.deleteSelectedFilesQuestion') : $t('routes.node.deleteAllFilesQuestion')
              }}
            </div>
            <div class="mt-2">
              <v-checkbox
                v-model="deletePermanently"
                density="compact"
                hide-details
                :label="$t('routes.node.deleteDialogDeletePermanently')"
              />
              <div v-if="deletePermanently">
                <div class="mt-2 tw-warning">
                  <v-icon>mdi-alert-box</v-icon>
                  <span v-if="nodes.length === 0">
                    {{ $t('routes.node.deleteSelectedDialogPermanentDeletionWarning') }}
                  </span>
                  <span v-else>
                    {{ $t('routes.node.deleteSelectedDialogNodesPermanentDeletionWarning') }}
                  </span>
                </div>
                <div v-if="nodes.length > 0" class="mt-2 tw-warning">
                  <v-icon>mdi-alert-box</v-icon>
                  <span>{{ $t('routes.node.deletePermanentlyNodeRecycleBinWarning') }}</span>
                </div>
              </div>
            </div>
            <div class="mt-2 tw-warning">
              <v-icon>mdi-alert-box</v-icon>
              <span>{{ deleteMessage }}</span>
            </div>
            <div class="mt-2 tw-warning">
              <v-icon>mdi-alert-box</v-icon>
              <span>{{ $t('routes.node.deleteDialogArchivedFilesCannotBeRestored') }}</span>
            </div>
          </div>
          <div v-if="errorMessage && errorCode !== 'ERR233'" class="bg-error">
            {{ errorMessage }}
          </div>
          <div v-if="errorCode === 'ERR233' && errorItems" class="bg-error my-2">
            {{ $t('backend.ERR233', errorParams) }}
            <span v-if="errorItems.length === 1">
              {{ $t('routes.node.depthExceededTooDeep', errorItems[0].children.length) }}
              <DepthExceededChildren :children="errorItems[0].children" @navigate="dialog = false" />
            </span>
            <template v-else>
              {{ $t('routes.node.depthExceededTooDeep', 2) }}
              <ul class="mx-5 mt-1">
                <li v-for="item in errorItems" :key="item.parent.id">
                  <strong>{{ item.parent.name }}</strong> &gt;
                  <DepthExceededChildren :children="item.children" @navigate="dialog = false" />
                </li>
              </ul>
            </template>
          </div>
          <div v-if="errorTranslatable" class="bg-error">
            {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
          </div>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!errorMessage && !errorTranslatable && affectedFtpUsers.length === 0"
            @click="deleteItems"
            elevation="0"
            class="btnOk"
            data-cy="dialog-deleteSelectedItems-ok"
            :disabled="deletionInProgress"
          >{{ $t('common.delete') }}
          </v-btn>
          <v-btn variant="text" @click="cancel" data-cy="dialog-deleteSelectedItems-false"
                 class="btnCancel">{{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>

    </div>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import DepthExceededChildren from '@/components/DepthExceededChildren.vue';

export default {
  mixins: [DialogActionKeys, DialogModel],
  components: { DepthExceededChildren },
  name: 'DeleteSelectedDialog',
  props: {
    nodes: {
      type: Array,
      required: true,
    },
    pods: {
      type: Array,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      required: false,
    },
    deletionType: {
      type: String,
      required: false,
    },
  },
  data: function() {
    return {
      errorMessage: '',
      errorTranslatable: '',
      errorCode: null,
      errorParams: {},
      errorItems: null,
      preventNoItems: false,
      waitingForUsersCount: false,
      userCount: 0,
      disabledUserCount: 0,
      deletionInProgress: false,
      deletePermanently: false,
      affectedFtpUsers: [],
    }
  },
  methods: {
    cancel: function() {
      this.dialog = false;
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.errorCode = null;
      this.errorParams = {};
      this.errorItems = null;
      this.waitingForUsersCount = false;
      this.userCount = 0;
      this.disabledUserCount = 0;
      this.affectedFtpUsers = [];
    },
    deleteItems: async function () {
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.preventNoItems = true;
      this.deletionInProgress = true;

      const nodeStore = useNodeStore();
      const podStore = usePodStore();

      const tenants = this.nodes.filter((node) => node.parentid && node.parentid === this.rootFolderId);
      const regularNodes = this.nodes.filter((node) => node.parentid !== 1);
      const promises = [];

      if (regularNodes.length > 0) {
        const nodeIDs = regularNodes.map(x => x.id);
        promises.push(nodeStore.deleteNodes(nodeIDs, this.deletePermanently).then((deletedNodes) => {
          if (Array.isArray(deletedNodes) && deletedNodes.length > 0) {
            podStore.removePodsByNodeDeleted(deletedNodes);
            nodeStore.nodeDeletedNotification(deletedNodes);
          }
        }));
      }

      if (tenants.length > 0) {
        const tenantIDs = tenants.map(x => x.id);
        promises.push(nodeStore.deleteTenants(tenantIDs));
      }

      if (this.pods.length > 0) {
        const podIds = this.pods.map(x => x.id);
        promises.push(podStore.deletePods(podIds, this.deletePermanently));
      }

      try {
        await Promise.all(promises);

        if (!this.deletePermanently) {
          podStore.refreshRecycleBin();
        }

        this.dialog = false;
        this.preventNoItems = true;
      } catch (error) {
        console.log('Error deleting select', error);
        this.preventNoItems = true;

        if (error?.response?.data?.error) {
          const err = error.response.data.error;
          if (err.code === 'ERR233') {
            this.errorCode = err.code;
            this.errorParams = { ...err.params };
            if (err.extra?.items) {
              this.errorItems = err.extra.items;
            }
          } else {
            this.errorMessage = this.$localizeMessage(err, this.$t('routes.pod.errorDeletingItems'));
          }
        } else {
          this.errorMessage = this.$t('routes.pod.errorDeletingItems');
        }
      }
    },
  },
  watch: {
    dialog: async function (newValue) {
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.deletionInProgress = false;
      this.deletePermanently = false;
      this.affectedFtpUsers = [];

      if (newValue) this.preventNoItems = false;
      if (newValue === true && this.nodes && this.nodes.length > 0) {
        this.waitingForUsersCount = true;

        const nodeIDs = this.nodes.map((node) => node.id);
        const nodeStore = useNodeStore();

        try {
          const [usersResult, ftpUsers] = await Promise.all([
            nodeStore.getUsersForNodes(nodeIDs),
            nodeStore.getAffectedFtpUsers(nodeIDs),
          ]);
          this.userCount = usersResult.enabled;
          this.disabledUserCount = usersResult.disabled;
          this.affectedFtpUsers = ftpUsers || [];
          this.waitingForUsersCount = false;
        } catch (error) {
          console.log('Error deleting selected', error);
          this.preventNoItems = true;

          if (error?.response?.data?.error) {
            this.errorMessage = this.$localizeMessage(error.response.data.error, this.$t('routes.pod.errorDeletingItems'));
          } else {
            this.errorMessage = this.$t('routes.pod.errorDeletingItems');
          }
        }
      }
    },
  },
  computed: {
    rootFolderId: function() {
      const commonStore = useCommonStore();
      return commonStore.rootFolderId;
    },
    affectedFtpUsersByNode: function() {
      const groups = {};
      for (const user of this.affectedFtpUsers) {
        if (!groups[user.nodeId]) {
          groups[user.nodeId] = { nodeId: user.nodeId, nodeName: user.nodeName, usernames: [] };
        }
        groups[user.nodeId].usernames.push(user.username);
      }
      return Object.values(groups);
    },
    shouldShowNoItems: function() {
      return !this.errorTranslatable && !this.errorMessage && !this.preventNoItems;
    },
    noThingsSelected: function() {
      return (!this.nodes || this.nodes.length === 0) && (!this.pods || this.pods.length === 0);
    },
    deleteMessage: function() {
      if (this.userCount === 0 && this.disabledUserCount === 0) {
        return this.$t('routes.node.deleteSelectedDialogMessageNoUsers');
      } else if (this.nodes.length === 1 && this.pods.length === 0) {
        if (this.nodes[0].parentid && this.nodes[0].parentid === this.rootFolderId) {
          if (this.userCount === 0 && this.disabledUserCount === 0) {
            return this.$t('routes.node.deleteSelectedDialogMessageNoUsers');
          } else {
            return this.$t('routes.node.deleteSelectedDialogMessageSingularTenant', this.userCount + this.disabledUserCount);
          }
        } else {
          if (this.userCount === 0) {
            return this.$t('routes.node.deleteSelectedDialogMessageNoUsers');
          } else {
            return this.$t('routes.node.deleteSelectedDialogMessageSingular', this.userCount);
          }
        }
      } else {
        for (const folder of this.nodes) {
          if (folder.parentid && folder.parentid === this.rootFolderId) {
            return this.$t('routes.node.deleteSelectedDialogMessageWithUsersAndTenants', this.userCount + this.disabledUserCount);
          }
        }
        return this.$t('routes.node.deleteSelectedDialogMessageWithUsers', this.userCount);
      }

    }
  }
}
</script>

<style>

</style>
